import React from "react";
import requiresAuth from "./requiresAuth";
import isProdReady from "./isProdReady";
import requiresClient from "./requiresClient";
import bindActionCreators from "../../utils/bindActionCreators";
import { USER_AUTH_KEY } from "../../utils/constants";
import jwtDecode from "jwt-decode";

import {
  clearSession,
  createSession,
  preventNextRedirect,
  SET_USER,
  SET_ERROR,
  setSelectedClient,
} from "./authActions";
import authReducer from "./authReducer";
import { AuthService } from "./authService";
import fetchMsEmailUser from "./fetchMsEmailUser";
import { fetchAuthSession } from "@aws-amplify/auth";

export const AuthStateContext = React.createContext();
export const AuthDispatchContext = React.createContext();

export const useAuthState = () => {
  const context = React.useContext(AuthStateContext);
  if (!context) {
    throw new Error("useAuthState must be used within an AuthProvider");
  }
  return context;
};

export const useAuthDispatch = () => {
  const dispatch = React.useContext(AuthDispatchContext);
  if (!dispatch) {
    throw new Error("useAuthDispatch must be used within an AuthProvider");
  }

  const state = useAuthState();
  const getState = () => state;

  return bindActionCreators(
    {
      clearSession,
      createSession,
      preventNextRedirect,
      setSelectedClient,
    },
    dispatch,
    getState,
  );
};

export const useAuth = () => [useAuthState(), useAuthDispatch()];

const AuthProvider = ({ children = null }) => {
  const sessionUser = window.localStorage.getItem(USER_AUTH_KEY);
  const user = sessionUser ? JSON.parse(sessionUser) : null;

  // FIX: Validate that the Cognito idToken is still valid before treating
  // the user as authenticated. Without this check, when a user closes a tab
  // and reopens after the token has expired (e.g. 3+ hours later), the app
  // would immediately render the authenticated Layout and fire API calls
  // (like /sources) with the stale expired token from localStorage — causing
  // 401s and a redirect loop back to /login.
  //
  // By gating on `tokenValid`, the AuthProvider will set user=null when the
  // token is expired, which causes `checkAndFetchUser()` below to run. That
  // calls `fetchAuthSession()` which waits for the Cognito /oauth2/token
  // exchange to complete and writes a fresh idToken to localStorage BEFORE
  // the authenticated UI (and its API calls) are rendered.
  const tokenValid = !AuthService.isTokenExpired();
  console.log(
    "Token valid:",
    tokenValid,
    "Expiry:",
    AuthService.getTokenExpiryFromStorage(),
  );
  const initialState = {
    error: null,
    isAuthenticated: user !== null && tokenValid,
    isCreatingSession: false,
    user: tokenValid ? user : null,
    redirectRoute: null,
  };

  const [state, dispatch] = React.useReducer(authReducer, initialState);
  const [isCheckingSession, setIsCheckingSession] = React.useState(false);

  // Check for Cognito session and fetch user if needed
  React.useEffect(() => {
    async function checkAndFetchUser() {
      // Skip if we already have a user or if we're already checking
      if (state.user || isCheckingSession) {
        return;
      }

      try {
        setIsCheckingSession(true);
        const { tokens } = await fetchAuthSession();

        if (tokens?.idToken) {
          const idToken = tokens.idToken;
          const user = await fetchMsEmailUser(jwtDecode(idToken.toString()));

          if (user) {
            dispatch({ type: SET_USER, user });
            window.localStorage.setItem(USER_AUTH_KEY, JSON.stringify(user));
          }
        }
      } catch (error) {
        console.error("❌ Error in AuthProvider:", error);
        // Dispatch error to state so CreateSession can show Access Denied
        dispatch({ type: SET_ERROR, error: error.message });
      } finally {
        setIsCheckingSession(false);
      }
    }

    checkAndFetchUser();
  }, []); // Run only once on mount

  // Update localStorage when user changes
  React.useEffect(() => {
    if (state.user) {
      window.localStorage.setItem(USER_AUTH_KEY, JSON.stringify(state.user));
    }
  }, [state.user]);

  return (
    <AuthStateContext.Provider value={state}>
      <AuthDispatchContext.Provider value={dispatch}>
        {children}
      </AuthDispatchContext.Provider>
    </AuthStateContext.Provider>
  );
};

export { AuthProvider as default, requiresAuth, requiresClient, isProdReady };
